import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import type { ThreadLista } from '../api/types';
import { displayPartecipante, nice_date } from '../utils';
import { PROBLEMA_TIPOLOGIA_RISPOSTA } from '../globals/auto-enums';
import { ProblemaTabella, Spoiler } from './';
import './TabellaProblemi.scss';

const SORT_OPTIONS = {
  id: { label: 'ID', value: (thread: ThreadLista) => thread.id },
  bellezza: { label: 'bellezza', value: (thread: ThreadLista) => thread.bellezza_medio },
  difficolta: { label: 'difficoltà', value: (thread: ThreadLista) => thread.difficolta_medio },
  assegnati: { label: 'correttori assegnati', value: (thread: ThreadLista) => thread.problema.correttori_assegnati.length },
  correzioni: { label: 'correzioni', value: (thread: ThreadLista) => thread.problema.correttori_eseguito.length },
  views: { label: 'visualizzazioni', value: (thread: ThreadLista) => thread.visualizzazioni },
  modifica: { label: 'ultima modifica', value: (thread: ThreadLista) => new Date(thread.ultima_modifica).getTime() },
};
type SortKey = keyof typeof SORT_OPTIONS;
export type ProblemColumn = Exclude<SortKey, 'id'> | 'tipo' | 'risposte' | 'autore';
export const SHORTLIST_COLUMNS: ProblemColumn[] = ['bellezza', 'difficolta', 'views', 'autore', 'modifica'];
const DEFAULT_COLUMNS: ProblemColumn[] = ['tipo', 'bellezza', 'difficolta', 'assegnati', 'correzioni', 'risposte', 'views', 'autore', 'modifica'];
const HEADINGS: Record<ProblemColumn, string> = {
  tipo: 'Tipo', bellezza: 'Bellezza', difficolta: 'Difficoltà', assegnati: 'Ass', correzioni: 'Corr',
  risposte: 'Risp', views: 'Views', autore: 'Proposto da', modifica: 'Last',
};
const DESCRIPTIONS: Partial<Record<ProblemColumn, string>> = {
  tipo: 'Tipologia risposta', assegnati: 'Persone che devono correggere il problema', correzioni: 'Correzioni',
  risposte: 'Giuste/Sbagliate', views: 'Visualizzazioni', autore: 'Proposto da', modifica: 'Ultima modifica',
};

interface ExtraColumn {
  header: React.ReactNode;
  render: (thread: ThreadLista, index: number) => React.ReactNode;
}
interface Props {
  threads: ThreadLista[];
  columns?: ProblemColumn[];
  ratings: 'spoiler' | 'visible' | 'hidden';
  currentParticipantId?: number;
  sortable?: boolean;
  leadingColumn?: ExtraColumn;
  actionColumn?: ExtraColumn;
  renderRow?: (thread: ThreadLista, index: number, cells: React.ReactNode) => React.ReactNode;
  renderBody?: (rows: React.ReactNode) => React.ReactNode;
}

const formatRating = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '—';

const TabellaProblemi = ({ threads, columns = DEFAULT_COLUMNS, ratings, currentParticipantId,
  sortable = true, leadingColumn, actionColumn, renderRow, renderBody }: Props) => {
  const [sorting, setSorting] = useState<{ key: SortKey; direction: 1 | -1 }>({ key: 'id', direction: -1 });
  const [showBeauty, setShowBeauty] = useState(false);
  const [showDifficulty, setShowDifficulty] = useState(false);
  const visibleColumns = columns.filter(column => ratings !== 'hidden' || (column !== 'bellezza' && column !== 'difficolta'));
  const sortedThreads = sortable ? [...threads].sort((first, second) =>
    sorting.direction * (SORT_OPTIONS[sorting.key].value(first) - SORT_OPTIONS[sorting.key].value(second))) : threads;

  const header = (key: SortKey, label: string, description?: string) => {
    if (!sortable) return <th key={key} scope="col" title={description}>{label}</th>;
    const active = sorting.key === key;
    const nextDirection = active && sorting.direction === -1 ? 'crescente' : 'decrescente';
    return <th key={key} scope="col" title={description}
      aria-sort={active ? (sorting.direction === 1 ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="problem-list-sort" title={`Ordina per ${SORT_OPTIONS[key].label}: ${nextDirection}`}
        onClick={() => setSorting(current => ({ key, direction: current.key === key && current.direction === -1 ? 1 : -1 }))}>
        <span>{label}</span>
        <i className={`fa ${active ? (sorting.direction === 1 ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`} aria-hidden="true" />
        <span className="sr-only"> — Ordina per {SORT_OPTIONS[key].label}: {nextDirection}</span>
      </button>
    </th>;
  };

  const cell = (column: ProblemColumn, item: ThreadLista) => {
    switch (column) {
      case 'tipo': {
        const icon = { num: 'calculator', open: 'brain', mul: 'list-check' }[item.problema.tipologia_risposta];
        const label = PROBLEMA_TIPOLOGIA_RISPOSTA.find(type => type.id === item.problema.tipologia_risposta)?.display || '';
        return <i title={label} aria-label={label} className={`fa fa-${icon}`} />;
      }
      case 'bellezza': {
        const value = formatRating(item.bellezza_medio);
        return ratings === 'visible' || showBeauty ? value : <Spoiler>{value}</Spoiler>;
      }
      case 'difficolta': {
        const value = `${formatRating(item.difficolta_medio)} (${formatRating(item.problema.difficolta_automatica)})`;
        return ratings === 'visible' || showDifficulty ? value : <Spoiler>{value}</Spoiler>;
      }
      case 'assegnati': return item.problema.correttori_assegnati.length;
      case 'correzioni': return <>{item.problema.correttori_eseguito.length}
        {currentParticipantId !== undefined && item.problema.correttori_eseguito.includes(currentParticipantId) && <i className="text-success fa fa-check" />}
      </>;
      case 'risposte': return <span title="Giusto/Sbagliato">{item.risposto_giusto}/{item.risposto_totale - item.risposto_giusto}</span>;
      case 'views': return item.visualizzazioni;
      case 'autore': return <Link to={`/utente/${item.autore}/`}>{displayPartecipante(item.autore)}<br /><small>{nice_date(item.data_inizio)}</small></Link>;
      case 'modifica': return <small>{nice_date(new Date(item.ultima_modifica))}</small>;
    }
  };

  const rows = sortedThreads.map((item, index) => {
    const cells = <>
      {leadingColumn && <td>{leadingColumn.render(item, index)}</td>}
      <td><ProblemaTabella item={item} /></td>
      {visibleColumns.map(column => <td key={column}>{cell(column, item)}</td>)}
      {actionColumn && <td>{actionColumn.render(item, index)}</td>}
    </>;
    return renderRow ? renderRow(item, index, cells) : <tr key={item.id}>{cells}</tr>;
  });

  return <div className="problem-threads-table">
    {ratings === 'spoiler' && <div className="problem-list-ratings mb-2" role="group" aria-label="Visibilità valutazioni">
      <span className="small text-muted">Valutazioni:</span>
      <button type="button" className={`btn btn-sm btn-outline-secondary${showBeauty ? ' active' : ''}`} aria-pressed={showBeauty}
        onClick={() => setShowBeauty(value => !value)}>
        <i className={`fa ${showBeauty ? 'fa-eye-slash' : 'fa-eye'} mr-1`} aria-hidden="true" />{showBeauty ? 'Nascondi bellezza' : 'Mostra bellezza'}
      </button>
      <button type="button" className={`btn btn-sm btn-outline-secondary${showDifficulty ? ' active' : ''}`} aria-pressed={showDifficulty}
        onClick={() => setShowDifficulty(value => !value)}>
        <i className={`fa ${showDifficulty ? 'fa-eye-slash' : 'fa-eye'} mr-1`} aria-hidden="true" />{showDifficulty ? 'Nascondi difficoltà' : 'Mostra difficoltà'}
      </button>
    </div>}
    <div className="table-responsive"><table className="table table-bordered table-hover table-striped">
      <thead className="thead-dark"><tr>
        {leadingColumn && <th scope="col">{leadingColumn.header}</th>}
        {header('id', '#ID - Titolo', 'Ordinamento per ID del problema')}
        {visibleColumns.map(column => column in SORT_OPTIONS
          ? header(column as SortKey, HEADINGS[column], DESCRIPTIONS[column])
          : <th key={column} scope="col" title={DESCRIPTIONS[column]}>{HEADINGS[column]}</th>)}
        {actionColumn && <th scope="col">{actionColumn.header}</th>}
      </tr></thead>
      {renderBody ? renderBody(rows) : <tbody>{rows}</tbody>}
    </table></div>
  </div>;
};

export default TabellaProblemi;
